// SkillsApp.tsx — a BOARD APP: the skill library, made browsable.
//
// Third of the "Board App" pattern (Erez, 2026-07-24): a board tile is not a
// static poster, it is a live application you zoom into. Asked for 2026-07-26:
// "sidebar, a detail page, and a home page where I can see all the skills with
// some filters in a nice gallery."
//
//   LEFT   — the sidebar: search, status + sort filters, and the full list.
//   RIGHT  — HOME is a gallery of every skill as a card; picking one swaps the
//            same pane for the DETAIL page, which renders the skill's real
//            BlockRenderer recipe. "All skills" returns you to the gallery.
//
// Reuses the EXISTING data path, no new endpoint: /api/skills for the list
// (name, description, status, body_chars, updated_at) and /api/skills/:slug for
// the body. New skills appear with no deploy.
//
// The React-Flow traps (learned on KnowledgeFeedBoard + MemoryApp): a
// non-draggable node gets pointer-events:none, which kills BOTH clicks and
// wheel inside it. So every interactive or scrolling element carries
// pointerEvents:"auto" plus the nowheel/nodrag/nopan classes. And `useApi()`
// returns a FRESH closure each render, so it is deliberately NOT in any
// dependency array — keying on the slug only is what stops an infinite refetch.

import { useEffect, useMemo, useState } from "react";
import { Loader2, Search, Sparkles, ChevronLeft } from "lucide-react";
import { useApi } from "@/lib/api";
import { architectureConfig } from "../blueprint/ArchitectureBlocks";
import { BlockRenderer, type Block } from "../blueprint/BlockRenderer";
import { bodyToBlocks } from "../blueprint/mdToBlocks";
import { ACCENT, CATEGORY_ORDER, UNCATEGORIZED, categoryOf, accentOf, tagsOf } from "./skillTaxonomy";

const ACCENT_FOR = (c: string) => ACCENT[c] ?? ACCENT[UNCATEGORIZED];

// editorial palette — matches the Jarvis command board (skill://design)
const T = {
  ink: "#1B211E", ink2: "#4A534D", faint: "#8C948E",
  card: "#FFFFFF", paper: "#FAF7F2", bone: "#F2EDE4",
  line: "#ECE7DF", hair: "#E3DED4",
  green: "#3F8F6F", greenDeep: "#2E6B52", greenWash: "#EEF5F1",
};
const FONT = "'Inter Tight', Inter, system-ui, -apple-system, sans-serif";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

// Dimensions and chrome deliberately MATCH AgentBrainApp (the Memory app) so the
// two read as siblings on the board rather than two different products: one
// rounded card, an internal divider instead of a gap, 300 + 1400 wide, H 1180.
const LIST_W = 300;
const MAIN_W = 1400;
const H = 1180;

type Skill = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  status: "draft" | "active" | "archived";
  body_chars: number;
  updated_at: string;
};
type SkillDetail = Skill & { body: unknown };

type Sort = "az" | "recent" | "biggest";
const SORTS: { id: Sort; label: string }[] = [
  { id: "az", label: "A-Z" },
  { id: "recent", label: "Recently updated" },
  { id: "biggest", label: "Deepest" },
];

// NOTE: `skills.body` is NOT one format. Measured 2026-07-26: 21 of 33 rows are
// JSON BlockRenderer recipes and 12 are plain markdown. Assuming recipe-only is
// what made the first version of this app render a title with an empty body.
// `bodyToBlocks` resolves either shape — see blueprint/mdToBlocks.ts.

function depthLabel(chars: number): string {
  if (chars >= 20000) return "deep";
  if (chars >= 8000) return "full";
  if (chars >= 2000) return "brief";
  return "stub";
}

function ago(iso: string): string {
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (d <= 0) return "today";
  if (d === 1) return "yesterday";
  if (d < 30) return `${d}d ago`;
  return `${Math.floor(d / 30)}mo ago`;
}

const chip = (on: boolean): React.CSSProperties => ({
  fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: "0.04em",
  textTransform: "uppercase", cursor: "pointer", pointerEvents: "auto",
  color: on ? T.greenDeep : T.faint,
  background: on ? T.greenWash : "transparent",
  border: `1px solid ${on ? T.green : T.hair}`,
  borderRadius: 8, padding: "4px 10px",
});

export function SkillsApp() {
  const api = useApi();
  const [skills, setSkills] = useState<Skill[] | "loading">("loading");
  const [slug, setSlug] = useState<string | null>(null);
  const [detail, setDetail] = useState<SkillDetail | "loading" | null>(null);

  const [q, setQ] = useState("");
  const [status, setStatus] = useState<"all" | "active" | "draft">("all");
  const [cat, setCat] = useState<string>("all");
  const [sort, setSort] = useState<Sort>("az");

  // The list. `api` is intentionally absent from the deps — see header.
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const r = await api("/api/skills");
        const rows = (await r.json()) as Skill[];
        if (!dead) setSkills(rows);
      } catch {
        if (!dead) setSkills([]);
      }
    })();
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The detail, keyed on the slug ONLY.
  useEffect(() => {
    if (!slug) { setDetail(null); return; }
    let dead = false;
    setDetail("loading");
    (async () => {
      try {
        const r = await api(`/api/skills/${slug}`);
        const row = (await r.json()) as SkillDetail;
        if (!dead) setDetail(row);
      } catch {
        if (!dead) setDetail(null);
      }
    })();
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const shown = useMemo(() => {
    if (skills === "loading") return [];
    const needle = q.trim().toLowerCase();
    const out = skills.filter(s => {
      if (status !== "all" && s.status !== status) return false;
      if (cat !== "all" && categoryOf(s.slug) !== cat) return false;
      if (!needle) return true;
      return (
        s.name.toLowerCase().includes(needle) ||
        s.slug.toLowerCase().includes(needle) ||
        (s.description || "").toLowerCase().includes(needle) ||
        tagsOf(s.slug).some(t => t.includes(needle))
      );
    });
    out.sort((a, b) =>
      sort === "az" ? a.name.localeCompare(b.name)
      : sort === "biggest" ? b.body_chars - a.body_chars
      : new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    );
    return out;
  }, [skills, q, status, cat, sort]);

  // Which categories actually have skills, in editorial order, plus a visible
  // Uncategorized bucket when something is unfiled.
  const cats = useMemo(() => {
    if (skills === "loading") return [];
    const present = new Set(skills.map(s => categoryOf(s.slug)));
    const out = CATEGORY_ORDER.filter(c => present.has(c)) as string[];
    if (present.has(UNCATEGORIZED)) out.push(UNCATEGORIZED);
    return out;
  }, [skills]);

  const loading = (
    <div style={{ padding: 26, display: "inline-flex", alignItems: "center", gap: 8, fontFamily: FONT, fontSize: 13, color: T.faint }}>
      <Loader2 className="animate-spin" style={{ width: 14, height: 14 }} /> Loading…
    </div>
  );

  return (
    <div
      className="nopan nodrag"
      style={{ display: "flex", width: LIST_W + MAIN_W, height: H, fontFamily: FONT, pointerEvents: "auto", background: T.card, border: `1px solid ${T.hair}`, borderRadius: 20, overflow: "hidden", boxShadow: "0 8px 24px rgba(25,29,27,0.05)" }}
    >
      {/* ── SIDEBAR ─────────────────────────────────────────── */}
      <aside
        className="nowheel nodrag nopan"
        style={{ width: LIST_W, flexShrink: 0, overflow: "auto", background: T.paper, borderRight: `1px solid ${T.line}`, padding: "22px 18px 26px", pointerEvents: "auto", boxSizing: "border-box" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <Sparkles style={{ width: 15, height: 15, color: T.green }} />
          <span style={{ fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.greenDeep }}>
            Skills
          </span>
        </div>
        <div style={{ fontSize: 12, color: T.faint, marginBottom: 16 }}>
          {skills === "loading" ? "…" : `${shown.length} of ${skills.length} · the living law`}
        </div>

        {/* search */}
        <div style={{ position: "relative", marginBottom: 14 }}>
          <Search style={{ width: 13, height: 13, color: T.faint, position: "absolute", left: 11, top: 10 }} />
          <input
            className="nodrag nopan"
            value={q}
            onChange={e => setQ(e.target.value)}
            onPointerDown={e => e.stopPropagation()}
            placeholder="Search skills…"
            style={{ fontFamily: FONT, fontSize: 13, color: T.ink, background: T.card, border: `1px solid ${T.hair}`, borderRadius: 9, padding: "8px 12px 8px 32px", width: "100%", boxSizing: "border-box", outline: "none", pointerEvents: "auto" }}
          />
        </div>

        {/* status filter */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
          {(["all", "active", "draft"] as const).map(s => (
            <button key={s} className="nodrag nopan" style={chip(status === s)}
              onPointerDown={e => e.stopPropagation()} onClick={() => setStatus(s)}>
              {s}
            </button>
          ))}
        </div>

        {/* category filter — the editorial overlay, colour-coded */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
          <button className="nodrag nopan" style={chip(cat === "all")}
            onPointerDown={e => e.stopPropagation()} onClick={() => setCat("all")}>
            every area
          </button>
          {cats.map(c => {
            const on = cat === c;
            const a = ACCENT_FOR(c);
            return (
              <button key={c} className="nodrag nopan"
                onPointerDown={e => e.stopPropagation()} onClick={() => setCat(c)}
                style={{ fontFamily: FONT, fontSize: 11.5, fontWeight: 600, cursor: "pointer", pointerEvents: "auto", display: "inline-flex", alignItems: "center", gap: 5, color: on ? T.card : T.ink2, background: on ? a : T.card, border: `1px solid ${on ? a : T.hair}`, borderRadius: 999, padding: "4px 9px" }}>
                <span style={{ width: 6, height: 6, borderRadius: 999, background: on ? T.card : a }} />
                {c}
              </button>
            );
          })}
        </div>

        {/* sort */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 18 }}>
          {SORTS.map(s => (
            <button key={s.id} className="nodrag nopan" style={chip(sort === s.id)}
              onPointerDown={e => e.stopPropagation()} onClick={() => setSort(s.id)}>
              {s.label}
            </button>
          ))}
        </div>

        {/* back to the gallery */}
        {slug && (
          <button
            className="nodrag nopan"
            onPointerDown={e => e.stopPropagation()}
            onClick={() => setSlug(null)}
            style={{ display: "inline-flex", alignItems: "center", gap: 5, fontFamily: FONT, fontSize: 12, fontWeight: 600, color: T.greenDeep, background: "transparent", border: "none", cursor: "pointer", pointerEvents: "auto", padding: 0, marginBottom: 14 }}
          >
            <ChevronLeft style={{ width: 13, height: 13 }} /> All skills
          </button>
        )}

        {/* the list */}
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {shown.map(s => {
            const on = s.slug === slug;
            return (
              <button
                key={s.id}
                className="nodrag nopan"
                onPointerDown={e => e.stopPropagation()}
                onClick={() => setSlug(s.slug)}
                style={{ textAlign: "left", cursor: "pointer", pointerEvents: "auto", background: on ? T.card : "transparent", border: `1px solid ${on ? T.hair : "transparent"}`, borderLeft: `2px solid ${on ? T.green : "transparent"}`, borderRadius: 8, padding: "8px 10px", fontFamily: FONT }}
              >
                <div style={{ fontSize: 13, fontWeight: on ? 600 : 500, color: T.ink, lineHeight: 1.3 }}>{s.name}</div>
                <div style={{ fontFamily: MONO, fontSize: 10, color: T.faint, marginTop: 2 }}>
                  {s.slug} · {depthLabel(s.body_chars)}
                </div>
              </button>
            );
          })}
          {skills !== "loading" && shown.length === 0 && (
            <div style={{ fontSize: 12, color: T.faint, padding: "10px 2px" }}>Nothing matches.</div>
          )}
        </div>
      </aside>

      {/* ── MAIN: gallery (home) or detail ──────────────────── */}
      <main
        className="nowheel nodrag nopan"
        style={{ flex: 1, minWidth: 0, overflow: "auto", background: T.card, pointerEvents: "auto", boxSizing: "border-box" }}
      >
        {skills === "loading" ? loading : slug ? (
          /* DETAIL */
          detail === "loading" ? loading : !detail ? (
            <div style={{ padding: 26, fontSize: 13, color: T.faint }}>Could not load that skill.</div>
          ) : (
            <div style={{ padding: "34px 40px 56px" }}>
              <div style={{ fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.greenDeep, marginBottom: 10 }}>
                skill://{detail.slug}
              </div>
              <h2 style={{ fontSize: 30, fontWeight: 700, color: T.ink, margin: "0 0 12px", letterSpacing: "-0.02em", lineHeight: 1.15 }}>
                {detail.name}
              </h2>
              {detail.description && (
                <p style={{ fontSize: 15, color: T.ink2, lineHeight: 1.65, margin: "0 0 8px", maxWidth: "72ch" }}>
                  {detail.description}
                </p>
              )}
              <div style={{ fontFamily: MONO, fontSize: 11, color: T.faint, marginBottom: 26 }}>
                {detail.status} · {depthLabel(detail.body_chars ?? 0)} · updated {ago(detail.updated_at)}
              </div>
              <div style={{ borderTop: `1px solid ${T.line}`, paddingTop: 26 }}>
                {(() => {
                  const blocks = bodyToBlocks(detail.body);
                  // The header above already carries name + description, so a
                  // recipe that opens with its own Hero would print the title
                  // twice — drop a leading Hero (same rule as SkillDetailPage).
                  const trimmed = blocks && blocks[0] && (blocks[0] as { type?: string }).type === "Hero"
                    ? blocks.slice(1) : blocks;
                  return trimmed && trimmed.length
                    ? <BlockRenderer config={architectureConfig} blocks={trimmed} />
                    : <div style={{ fontSize: 13, color: T.faint }}>This skill has no rendered body yet.</div>;
                })()}
              </div>
            </div>
          )
        ) : (
          /* HOME — the gallery */
          <div style={{ padding: "30px 32px 40px" }}>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: T.ink, margin: "0 0 4px", letterSpacing: "-0.01em" }}>
              The skill library
            </h2>
            <p style={{ fontSize: 13, color: T.ink2, margin: "0 0 24px" }}>
              Every skill is living law — it absorbs a correction the same beat it lands. Pick one to read it.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(258px, 1fr))", gap: 14 }}>
              {shown.map(s => (
                <button
                  key={s.id}
                  className="nodrag nopan"
                  onPointerDown={e => e.stopPropagation()}
                  onClick={() => setSlug(s.slug)}
                  style={{ textAlign: "left", cursor: "pointer", pointerEvents: "auto", background: T.paper, border: `1px solid ${T.hair}`, borderRadius: 14, padding: "16px 16px 14px", fontFamily: FONT, display: "flex", flexDirection: "column", gap: 7, minHeight: 132 }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontFamily: FONT, fontSize: 10.5, fontWeight: 600, color: accentOf(s.slug) }}>
                      <span style={{ width: 6, height: 6, borderRadius: 999, background: accentOf(s.slug) }} />
                      {categoryOf(s.slug)}
                    </span>
                    <span style={{ fontFamily: MONO, fontSize: 10, color: T.faint }}>{ago(s.updated_at)}</span>
                  </div>
                  <div style={{ fontSize: 14.5, fontWeight: 600, color: T.ink, lineHeight: 1.28 }}>{s.name}</div>
                  <div style={{ fontSize: 12, color: T.ink2, lineHeight: 1.5, flex: 1 }}>
                    {(s.description || "").slice(0, 124)}{(s.description || "").length > 124 ? "…" : ""}
                  </div>
                  {tagsOf(s.slug).length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {tagsOf(s.slug).map(t => (
                        <span key={t} style={{ fontFamily: MONO, fontSize: 9.5, color: T.ink2, background: T.bone, border: `1px solid ${T.hair}`, borderRadius: 5, padding: "1.5px 5px" }}>
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, fontFamily: MONO, fontSize: 10, color: T.faint, borderTop: `1px solid ${T.line}`, paddingTop: 7 }}>
                    <span>skill://{s.slug}</span>
                    <span>{depthLabel(s.body_chars)}</span>
                  </div>
                </button>
              ))}
            </div>
            {shown.length === 0 && (
              <div style={{ fontSize: 13, color: T.faint, paddingTop: 12 }}>Nothing matches those filters.</div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default SkillsApp;
